// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import { createDownloadJob, modifyJob } from '../api/jobs'
import { DOWNLOAD_TASK_NAME, DOWNLOAD_TASK_TYPE } from '../constants'
import moment from 'moment'
import md5 from 'md5'

const PRE_TASK_TEMPLATE = {
  startDate: null,
  jobName: null,
  jobType: 0, // camera only
  endDate: null,
  startTime: moment()
    .hour(9)
    .minute(0)
    .second(0),
  endTime: moment()
    .hour(18)
    .minute(0)
    .second(0),
  deviceID: null,
  roadID: null,
  geoFence: null,
  roadCoordinates: [],
  isAddButtonShow: null
}

const isEditingPreTaskValid = editingPreTask => {
  const { jobType, startDate, endDate, geoFence, deviceID, roadID, roadCoordinates } = editingPreTask
  const argValid = !!(startDate && endDate)
  let typeRelatedDataValid = false
  if (jobType === DOWNLOAD_TASK_TYPE.GEOFENCE) {
    try {
      typeRelatedDataValid = geoFence && geoFence.geometry.coordinates[0].length
    } catch (e) {
      typeRelatedDataValid = false
    }
  }
  if (jobType === DOWNLOAD_TASK_TYPE.DEVICE) {
    typeRelatedDataValid = Boolean(deviceID)
  }
  if (jobType === DOWNLOAD_TASK_TYPE.ROAD) {
    typeRelatedDataValid = !!roadID && roadCoordinates.length
  }
  return argValid && typeRelatedDataValid
}

function createPreTaskListItem (task) {
  const jobName = DOWNLOAD_TASK_NAME[task.jobType] || ''
  let id = 'invalid'
  if (task.deviceID) {
    id = task.deviceID
  } else if (task.roadID) {
    id = task.roadID
  } else if (task.geoFence && task.geoFence.length > 0) {
    id = md5(JSON.stringify(task.geoFence))
  }
  task.jobName = jobName.toUpperCase() + '-' + id
  return task
}

export type downloadState = {
  preTaskList: any[]
  editingPreTask: any
  pendingDownloadList: any[]
  isDownloadShow: boolean
  isDownloadPreTaskShow: boolean
  isDownloadPendingListShow: boolean
  mode: 'add' | 'update'
}

export const download = {
  state: {
    preTaskList: [],
    editingPreTask: { ...PRE_TASK_TEMPLATE },
    pendingDownloadList: [],
    isDownloadShow: true,
    isDownloadPreTaskShow: true,
    isDownloadPendingListShow: false,
    mode: 'add' // add | update
  },
  reducers: {
    setDownloadShow: (state, isDownloadShow: boolean) => ({ ...state, isDownloadShow }),
    updateEditingPreTask (state, task: any) {
      task = { ...state.editingPreTask, ...task }
      if (task.jobType !== undefined) {
        if (task.jobType !== DOWNLOAD_TASK_TYPE.ROAD) {
          task.roadID = null
          task.roadCoordinates = null
        }
        if (task.jobType !== DOWNLOAD_TASK_TYPE.DEVICE) {
          task.deviceID = null
        }
        if (task.jobType !== DOWNLOAD_TASK_TYPE.GEOFENCE) {
          task.geoFence = null
        }
      }
      if (!task.startTime) {
        task.startTime = moment()
          .hour(9)
          .minute(0)
          .second(0)
      }
      if (!task.endTime) {
        task.endTime = moment()
          .hour(18)
          .minute(0)
          .second(0)
      }
      return {
        ...state,
        editingPreTask: {
          ...task,
          isAddButtonShow: isEditingPreTaskValid(task) && state.mode === 'add'
        }
      }
    },
    clearEditingPreTask: (state, task: any) => ({ ...state, editingPreTask: { ...PRE_TASK_TEMPLATE } }),
    setDownloadPreTaskShow: (state, isDownloadPreTaskShow: boolean) => ({ ...state, isDownloadPreTaskShow }),
    setDownloadPendingListShow: (state, isDownloadPendingListShow: boolean) => ({
      ...state,
      isDownloadPendingListShow
    }),
    addToPreTaskList: (state, task: any) => {
      const item = createPreTaskListItem(task)
      return {
        ...state,
        editingPreTask: { ...state.editingPreTask },
        preTaskList: [...state.preTaskList, item]
      }
    },
    clearDownloadPreTaskList: state => ({ ...state, preTaskList: [] }),
    removePreTask: (state, index: number) => {
      let { preTaskList } = state
      preTaskList = [...preTaskList]
      preTaskList.splice(index, 1)
      return {
        ...state,
        preTaskList
      }
    },
    setEditingTask (state, task: any) {
      return {
        ...state,
        editingPreTask: { ...state.editingPreTask, ...task },
        mode: 'update'
      }
    },
    resetMode (state) {
      return {
        ...state,
        mode: 'add'
      }
    },
    clear () {
      return {
        preTaskList: [],
        editingPreTask: { ...PRE_TASK_TEMPLATE },
        pendingDownloadList: [],
        isDownloadShow: true,
        isDownloadPreTaskShow: true,
        isDownloadPendingListShow: false,
        mode: 'add' // add | update
      }
    }
  },
  effects: dispatch => ({
    async createDownloadTask (payload, rootState) {
      // format Params for request
        const {cameras,download} = rootState
        const {deviceID, groupID, cityID} = cameras.uploadingCamera || download.editingPreTask
        const task = {
          ...rootState.download.editingPreTask,
          deviceID,
          groupID,
          ...payload
        }
        // format points for request params
      const jobName = DOWNLOAD_TASK_NAME[task.jobType]
      task.jobName = `${jobName}-${deviceID}`
      if (task.geoFence) {
          task.points = task.geoFence.geometry.coordinates[0].map(p => ({ lon: p[0], lat: p[1] }))
        }
        if (task.jobType === DOWNLOAD_TASK_TYPE.ROAD) {
          task.points = task.roadCoordinates.map(p => ({ lon: p[0], lat: p[1] }))
        }
        task.startDate = moment(task.startDate).format('YYYY-MM-DD')
        task.endDate = moment(task.endDate).format('YYYY-MM-DD')
        task.startTime = moment(task.startTime).format('HH:mm:ss')
        task.endTime = moment(task.endTime).format('HH:mm:ss')
        if (task.roadID) {
          task.roadID = task.roadID.toString()
        }
        //task.cityID = rootState.city.current.id
        task.cityID = cityID
        try {
          await createDownloadJob(task)
          // dispatch.download.removePreTask(0)
        } catch (e) {
          console.log(e)
          throw new Error(e)
        }
      // ...
    },
    async updateDownloadTask (payload, rootState) {
      const { editingPreTask } = rootState.download
      const preTask = editingPreTask
      const task = {
        jobID: preTask.jobID,
        startDate: preTask.startDate.format('YYYY-MM-DD'),
        endDate: preTask.endDate.format('YYYY-MM-DD'),
        startTime: preTask.startTime.format('HH:mm:ss'),
        endTime: preTask.endTime.format('HH:mm:ss')
      }
      try {
        await modifyJob(task)
        // dispatch.download.removePreTask(0)
      } catch (e) {
        console.log(e)
        throw new Error(e)
      }
    },
    async onCameraClick (camera, rootState) {
      dispatch.download.updateEditingPreTask({ deviceID: camera.deviceID })
    },
    async changeTaskType (jobType, rootState) {
      dispatch.download.updateEditingPreTask({ jobType })
      if (jobType === DOWNLOAD_TASK_TYPE.DEVICE) {
        dispatch.cameraFinder.setVisible(true)
      } else {
        dispatch.cameraFinder.setVisible(false)
        dispatch.gpsTrace.setGPSTrackerVisible(false)
        dispatch.cameraFinder.setExpanded(false)
      }
      if (jobType === DOWNLOAD_TASK_TYPE.ROAD) {
        await dispatch.road.fetchRoadData()
      }
    }
  })
}
